import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import RuleRoundedIcon from "@mui/icons-material/RuleRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

import { can } from "../../../auth/can";
import { useAuth } from "../../../auth/useAuth";
import {
  buildAuthHeaders,
  getErrorMessage,
  getRequest,
  patchRequest,
  postRequest,
  PROJECTS_API,
  SURVEYS_API,
} from "./api";
import {
  actionButtonSx,
  PageShell,
  panelSx,
  statusChip,
  SummaryCard,
} from "./ProjectsCommon";

const emptyAssignmentForm = {
  template_id: "",
  project_id: "",
  site_id: "",
  survey_type: "PRE_INSTALL",
  is_required: true,
  allow_multiple_submissions: false,
  must_complete_before_commissioning: false,
  is_active: true,
};

const SURVEY_TYPES = ["PRE_INSTALL", "POST_INSTALL", "MAINTENANCE", "AUDIT"];
const TEMPLATE_STATUSES = ["DRAFT", "PUBLISHED", "ARCHIVED"];
const FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "dropdown",
  "radio",
  "checkbox",
  "boolean",
  "date",
  "image",
  "gps",
];
const OPTION_FIELD_TYPES = ["dropdown", "radio", "checkbox"];
const HELP_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "dropdown",
  "radio",
  "checkbox",
  "date",
];
const PLACEHOLDER_FIELD_TYPES = ["text", "textarea", "number"];
const VALIDATION_FIELD_TYPES = ["text", "textarea", "number"];
const SETTING_FIELD_TYPES = ["image", "gps"];

const defaultTemplateSchema = {
  name: "IFD Water Pump Site Survey",
  code: "IFD_WATER_PUMP_SURVEY_V1",
  version: 1,
  survey_type: "PRE_INSTALL",
  settings: {
    allow_draft: true,
    allow_offline: true,
  },
  sections: [
    {
      key: "site_capture",
      title: "Site Capture",
      description: "Capture location and visual proof from the site.",
      fields: [
        {
          key: "site_photo",
          label: "Site Photo",
          type: "image",
          required: true,
          settings: { max_files: 3 },
        },
        {
          key: "pump_photo",
          label: "Pump Photo",
          type: "image",
          required: true,
          settings: { max_files: 3 },
        },
        {
          key: "panel_photo",
          label: "Electrical Panel Photo",
          type: "image",
          required: false,
          settings: { max_files: 2 },
        },
        {
          key: "site_gps",
          label: "GPS",
          type: "gps",
          required: true,
          settings: { auto_capture: true, allow_manual_override: true },
        },
      ],
    },
    {
      key: "pump_details",
      title: "Pump Details",
      fields: [
        {
          key: "pump_location",
          label: "Pump Location / Landmark",
          type: "text",
          required: true,
          placeholder: "Pump room, borewell point, overhead tank, etc.",
        },
        {
          key: "pump_type",
          label: "Pump Type",
          type: "dropdown",
          required: true,
          options: [
            { label: "Submersible", value: "SUBMERSIBLE" },
            { label: "Monoblock", value: "MONOBLOCK" },
            { label: "Openwell", value: "OPENWELL" },
            { label: "Vertical Turbine", value: "VERTICAL_TURBINE" },
            { label: "Booster", value: "BOOSTER" },
          ],
        },
        {
          key: "pump_count",
          label: "Number of Pumps",
          type: "number",
          required: true,
          placeholder: "Enter total pumps at site",
        },
        {
          key: "pump_capacity_hp",
          label: "Pump Capacity (HP)",
          type: "number",
          required: true,
          placeholder: "Enter motor/pump HP",
        },
        {
          key: "water_source",
          label: "Water Source",
          type: "dropdown",
          required: true,
          options: [
            { label: "Borewell", value: "BOREWELL" },
            { label: "Sump", value: "SUMP" },
            { label: "Overhead Tank", value: "OHT" },
            { label: "River / Canal", value: "RIVER_CANAL" },
            { label: "Other", value: "OTHER" },
          ],
        },
      ],
    },
    {
      key: "electrical_and_control",
      title: "Electrical and Control",
      fields: [
        {
          key: "phase_type",
          label: "Phase Type",
          type: "radio",
          required: true,
          options: [
            { label: "Single Phase", value: "SINGLE_PHASE" },
            { label: "Three Phase", value: "THREE_PHASE" },
          ],
        },
        {
          key: "power_available",
          label: "Power Available at Site",
          type: "boolean",
          required: true,
        },
        {
          key: "control_panel_available",
          label: "Control Panel Available",
          type: "boolean",
          required: true,
        },
        {
          key: "motor_nameplate_photo",
          label: "Motor Nameplate Photo",
          type: "image",
          required: false,
          settings: { max_files: 2 },
        },
      ],
    },
    {
      key: "observations",
      title: "Observations",
      fields: [
        {
          key: "site_remarks",
          label: "Site Remarks",
          type: "textarea",
          required: false,
          placeholder:
            "Any access issue, civil work need, safety concern, or installation note",
        },
      ],
    },
  ],
};

const emptyTemplateForm = {
  name: "",
  code: "",
  description: "",
  survey_type: "PRE_INSTALL",
  status: "DRAFT",
  version: 1,
  schema_json: JSON.stringify(defaultTemplateSchema, null, 2),
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAnswerValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) {
    if (!value.length) return "-";
    return value
      .map((item) => {
        if (typeof item === "object") {
          return item.file_url || JSON.stringify(item);
        }
        return String(item);
      })
      .join(", ");
  }
  if (typeof value === "object") {
    if ("latitude" in value && "longitude" in value) {
      return `${value.latitude}, ${value.longitude}${value.address_text ? ` • ${value.address_text}` : ""}`;
    }
    if ("file_url" in value) {
      return value.file_url;
    }
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function slugify(value, fallback = "item") {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned || fallback;
}

function createField(type = "text", label = "") {
  const base = {
    key: `${slugify(label || type, type)}_${Math.random().toString(36).slice(2, 6)}`,
    label:
      label || `New ${type.replace(/^\w/, (char) => char.toUpperCase())} Field`,
    type,
    required: false,
  };

  if (PLACEHOLDER_FIELD_TYPES.includes(type)) {
    base.placeholder = "";
  }
  if (OPTION_FIELD_TYPES.includes(type)) {
    base.options = [
      { label: "Option 1", value: "OPTION_1" },
      { label: "Option 2", value: "OPTION_2" },
    ];
  }
  if (type === "image") {
    base.settings = { max_files: 1 };
  }
  if (type === "gps") {
    base.settings = { auto_capture: true, allow_manual_override: true };
  }
  if (type === "number") {
    base.validation = {};
  }

  return base;
}

function createSection(title = "New Section") {
  return {
    key: `${slugify(title, "section")}_${Math.random().toString(36).slice(2, 6)}`,
    title,
    description: "",
    fields: [createField("text", "New Field")],
  };
}

function normalizeTemplateSchema(schema, fallbackForm = emptyTemplateForm) {
  const base =
    schema && typeof schema === "object"
      ? cloneJson(schema)
      : cloneJson(defaultTemplateSchema);
  return {
    name: base.name || fallbackForm.name || "New Site Survey",
    code: base.code || fallbackForm.code || "SITE_SURVEY_V1",
    version: Number(base.version || fallbackForm.version || 1),
    survey_type: base.survey_type || fallbackForm.survey_type || "PRE_INSTALL",
    settings: {
      allow_draft: base.settings?.allow_draft !== false,
      allow_offline: base.settings?.allow_offline !== false,
    },
    sections:
      Array.isArray(base.sections) && base.sections.length
        ? base.sections.map((section, sectionIndex) => ({
            key: section.key || `section_${sectionIndex + 1}`,
            title: section.title || `Section ${sectionIndex + 1}`,
            description: section.description || "",
            fields:
              Array.isArray(section.fields) && section.fields.length
                ? section.fields.map((field, fieldIndex) => ({
                    ...field,
                    key:
                      field.key ||
                      `field_${sectionIndex + 1}_${fieldIndex + 1}`,
                    label: field.label || `Field ${fieldIndex + 1}`,
                    type: field.type || "text",
                    required: Boolean(field.required),
                    options: Array.isArray(field.options) ? field.options : [],
                    settings: field.settings || {},
                    validation: field.validation || {},
                    placeholder: field.placeholder || "",
                    help_text: field.help_text || "",
                    default_value: field.default_value ?? "",
                  }))
                : [createField("text", "New Field")],
          }))
        : [createSection()],
  };
}

function buildSchemaFromBuilder(builder, form) {
  return {
    name: form.name || builder.name || "New Site Survey",
    code: form.code || builder.code || "SITE_SURVEY_V1",
    version: Number(form.version || builder.version || 1),
    survey_type: form.survey_type || builder.survey_type || "PRE_INSTALL",
    settings: {
      allow_draft: builder.settings?.allow_draft !== false,
      allow_offline: builder.settings?.allow_offline !== false,
    },
    sections: (builder.sections || []).map((section) => ({
      key: section.key,
      title: section.title,
      description: section.description || undefined,
      fields: (section.fields || []).map((field) => {
        const payload = {
          key: field.key,
          label: field.label,
          type: field.type,
          required: Boolean(field.required),
        };

        if (field.placeholder) payload.placeholder = field.placeholder;
        if (field.help_text) payload.help_text = field.help_text;
        if (
          field.default_value !== "" &&
          field.default_value !== undefined &&
          field.default_value !== null
        ) {
          payload.default_value = field.default_value;
        }
        if (OPTION_FIELD_TYPES.includes(field.type)) {
          payload.options = (field.options || [])
            .filter((option) => option.label || option.value)
            .map((option) => ({
              label: option.label || option.value,
              value:
                option.value || slugify(option.label, "option").toUpperCase(),
            }));
        }
        if (field.type === "image") {
          payload.settings = {
            max_files: Number(field.settings?.max_files || 1),
          };
        }
        if (field.type === "gps") {
          payload.settings = {
            auto_capture: field.settings?.auto_capture !== false,
            allow_manual_override:
              field.settings?.allow_manual_override !== false,
          };
        }
        if (
          field.type === "number" ||
          field.type === "text" ||
          field.type === "textarea"
        ) {
          const validation = {};
          if (
            field.validation?.min !== "" &&
            field.validation?.min !== undefined
          )
            validation.min = Number(field.validation.min);
          if (
            field.validation?.max !== "" &&
            field.validation?.max !== undefined
          )
            validation.max = Number(field.validation.max);
          if (Object.keys(validation).length) payload.validation = validation;
        }
        return payload;
      }),
    })),
  };
}

function TemplateBuilderPreview({ schema }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid #E2E8F0",
        backgroundColor: "#F8FAFC",
        position: "sticky",
        top: 0,
        mt: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <PreviewRoundedIcon sx={{ color: "#2563EB" }} />
        <Typography sx={{ fontWeight: 900, color: "#0F172A" }}>
          Live Preview
        </Typography>
      </Stack>
      <Typography
        sx={{ mt: 1.25, fontSize: 18, fontWeight: 900, color: "#0F172A" }}
      >
        {schema.name || "Untitled Survey"}
      </Typography>
      <Stack
        direction="row"
        spacing={0.8}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1 }}
      >
        <Chip
          size="small"
          label={schema.survey_type?.replace(/_/g, " ")}
          sx={{ fontWeight: 800 }}
        />
        <Chip
          size="small"
          label={`${schema.sections?.length || 0} sections`}
          sx={{ fontWeight: 800 }}
        />
      </Stack>
      <Stack spacing={1.35} sx={{ mt: 2 }}>
        {(schema.sections || []).map((section, sectionIndex) => (
          <Paper
            key={section.key || sectionIndex}
            elevation={0}
            sx={{
              p: 1.35,
              borderRadius: 2.5,
              border: "1px solid #D7E0EA",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 900,
                color: "#64748B",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Section {sectionIndex + 1}
            </Typography>
            <Typography sx={{ mt: 0.45, fontWeight: 900, color: "#0F172A" }}>
              {section.title || "Untitled Section"}
            </Typography>
            {section.description ? (
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 12.5,
                  color: "#64748B",
                  lineHeight: 1.6,
                }}
              >
                {section.description}
              </Typography>
            ) : null}
            <Stack spacing={0.9} sx={{ mt: 1.15 }}>
              {(section.fields || []).map((field, fieldIndex) => (
                <Box
                  key={field.key || fieldIndex}
                  sx={{
                    p: 1.05,
                    borderRadius: 2,
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                      {field.label || "Untitled Field"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#64748B",
                        textTransform: "uppercase",
                      }}
                    >
                      {field.type}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.8}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.75 }}
                  >
                    {field.required ? (
                      <Chip
                        size="small"
                        label="Required"
                        sx={{
                          fontWeight: 800,
                          backgroundColor: "#FEF2F2",
                          color: "#B91C1C",
                        }}
                      />
                    ) : null}
                    {field.placeholder ? (
                      <Chip
                        size="small"
                        label={`Hint: ${field.placeholder}`}
                        sx={{ fontWeight: 800 }}
                      />
                    ) : null}
                    {field.type === "image" ? (
                      <Chip
                        size="small"
                        label={`${field.settings?.max_files || 1} photos`}
                        sx={{ fontWeight: 800 }}
                      />
                    ) : null}
                    {OPTION_FIELD_TYPES.includes(field.type) ? (
                      <Chip
                        size="small"
                        label={`${field.options?.length || 0} options`}
                        sx={{ fontWeight: 800 }}
                      />
                    ) : null}
                    {field.type === "gps" && field.settings?.auto_capture ? (
                      <Chip
                        size="small"
                        label="Auto GPS"
                        sx={{ fontWeight: 800 }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}

function SectionPanel({ eyebrow, title, children }) {
  return (
    <Paper elevation={0} sx={{ ...panelSx, p: 2.2 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 900,
          color: "#64748B",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          mt: 0.45,
          mb: 1.6,
          fontSize: 18,
          fontWeight: 900,
          color: "#0F172A",
          letterSpacing: "-0.03em",
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}
function SurveysSkeleton() {
  const responseRows = Array.from({ length: 4 });
  const summaryCards = Array.from({ length: 4 });
  const sideCards = Array.from({ length: 3 });

  return (
    <Stack spacing={2.25}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8} sx={{ width: "100%" }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.2 }}>
            <Skeleton variant="text" width="18%" height={18} />
            <Skeleton variant="text" width="38%" height={30} sx={{ mb: 1.6 }} />
            <Stack direction={{ xs: "column", lg: "row" }} spacing={1.2}>
              <Skeleton
                variant="rounded"
                width="100%"
                height={56}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton
                variant="rounded"
                width="100%"
                height={56}
                sx={{ borderRadius: 2 }}
              />
            </Stack>

            <Stack spacing={1.3} sx={{ mt: 2 }}>
              {responseRows.map((_, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={1.4}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="44%" height={26} />
                      <Skeleton variant="text" width="62%" height={20} />
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Skeleton
                        variant="rounded"
                        width={92}
                        height={26}
                        sx={{ borderRadius: 999 }}
                      />
                      <Skeleton
                        variant="rounded"
                        width={116}
                        height={36}
                        sx={{ borderRadius: 2 }}
                      />
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4} sx={{ width: "100%" }}>
          <Stack spacing={2}>
            {sideCards.map((_, index) => (
              <Paper key={index} elevation={0} sx={{ ...panelSx, p: 2.2 }}>
                <Skeleton variant="text" width="34%" height={18} />
                <Skeleton variant="text" width="62%" height={30} />
                <Divider sx={{ my: 1.4 }} />
                <Stack spacing={1}>
                  <Skeleton variant="text" width="88%" height={22} />
                  <Skeleton variant="text" width="74%" height={22} />
                  <Skeleton variant="text" width="56%" height={22} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        {summaryCards.map((_, index) => (
          <Paper key={index} elevation={0} sx={{ ...panelSx, p: 2 }}>
            <Skeleton
              variant="rounded"
              width={42}
              height={42}
              sx={{ borderRadius: 2.5 }}
            />
            <Skeleton variant="text" width="42%" height={18} sx={{ mt: 1.2 }} />
            <Skeleton variant="text" width="30%" height={40} />
            <Skeleton variant="text" width="74%" height={20} />
          </Paper>
        ))}
      </Box>
    </Stack>
  );
}

function TemplateCard({ template, onEdit, canEdit }) {
  const sections = template.schema_json?.sections || [];
  const fields = sections.reduce(
    (sum, section) => sum + (section.fields?.length || 0),
    0,
  );
  return (
    <Card elevation={0} sx={{ ...panelSx, height: "100%" }}>
      <CardContent sx={{ p: 2.2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 17, fontWeight: 900, color: "#0F172A" }}
            >
              {template.name}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontSize: 12.5,
                color: "#64748B",
                wordBreak: "normal",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
              }}
            >
              {template.code} • v{template.version}
            </Typography>
          </Box>
          {statusChip(template.status)}
        </Stack>
        <Typography
          sx={{
            mt: 1.25,
            fontSize: 13,
            color: "#475569",
            lineHeight: 1.65,
            minHeight: 44,
          }}
        >
          {template.description || "No survey description added yet."}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1.5, gap: 1 }}
          flexWrap="wrap"
        >
          <Chip
            size="small"
            label={template.survey_type.replace(/_/g, " ")}
            sx={{ fontWeight: 800 }}
          />
          <Chip
            size="small"
            label={`${sections.length} sections`}
            sx={{ fontWeight: 800 }}
          />
          <Chip
            size="small"
            label={`${fields} fields`}
            sx={{ fontWeight: 800 }}
          />
        </Stack>
        {canEdit ? (
          <Button
            onClick={() => onEdit(template)}
            sx={{ mt: 1.5, fontWeight: 800, textTransform: "none" }}
          >
            Edit Template
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function ProjectsSurveys() {
  const { activeOrg, permissions } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const queryProjectId =
    new URLSearchParams(location.search).get("project_id") || "";
  const querySiteId = new URLSearchParams(location.search).get("site_id") || "";
  const showBackToSites = Boolean(location.state?.fromSitesSurvey);
  const routeProjectName = String(location.state?.projectName || "").trim();
  const routeSiteName = String(location.state?.siteName || "").trim();

  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(queryProjectId);
  const [selectedSiteId, setSelectedSiteId] = useState(querySiteId);
  const [siteResponses, setSiteResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm);
  const [templateBuilder, setTemplateBuilder] = useState(() =>
    normalizeTemplateSchema(defaultTemplateSchema, emptyTemplateForm),
  );
  const [templateBuilderTab, setTemplateBuilderTab] = useState("builder");
  const [workspaceTab, setWorkspaceTab] = useState("responses");
  const [assignmentForm, setAssignmentForm] = useState(emptyAssignmentForm);
  const canCreateTemplate = can(permissions, "projects.survey.create");
  const canUpdateTemplate = can(permissions, "projects.survey.update");
  const canAssignTemplate = can(permissions, "projects.survey.assign");

  const validateAssignment = () => {
    const newErrors = {};

    if (!assignmentForm.template_id) newErrors.template_id = "Required";

    if (!assignmentForm.project_id) newErrors.project_id = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTemplate = () => {
    const newErrors = {};

    if (!templateForm.name) newErrors.name = "Required";
    if (!templateForm.code) newErrors.code = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadData = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const [templateData, assignmentData, projectData, siteData] =
        await Promise.all([
          getRequest(`${SURVEYS_API}/templates`, token, activeOrg),
          getRequest(`${SURVEYS_API}/assignments`, token, activeOrg),
          getRequest(`${PROJECTS_API}/deployments`, token, activeOrg),
          getRequest(`${PROJECTS_API}/sites`, token, activeOrg),
        ]);
      setTemplates(templateData?.templates || []);
      setAssignments(assignmentData?.assignments || []);
      setProjects(projectData?.projects || []);
      setSites(siteData?.sites || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load surveys workspace"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  const loadSiteResponses = useCallback(async () => {
    if (!token || !activeOrg?.id || !selectedSiteId) {
      setSiteResponses([]);
      return;
    }
    try {
      const data = await getRequest(
        `${SURVEYS_API}/sites/${selectedSiteId}/responses`,
        token,
        activeOrg,
      );
      setSiteResponses(data?.responses || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load site survey responses"));
    }
  }, [token, activeOrg, selectedSiteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSiteResponses();
  }, [loadSiteResponses]);

  useEffect(() => {
    if (!error && !success) return undefined;

    const timer = window.setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [error, success]);

  const selectedProject =
    projects.find((project) => project.id === selectedProjectId) || null;
  const visibleSites = useMemo(
    () =>
      sites.filter(
        (site) => !selectedProjectId || site.project_id === selectedProjectId,
      ),
    [sites, selectedProjectId],
  );
  const selectedSite =
    visibleSites.find((site) => site.id === selectedSiteId) || null;
  const headerProjectName = selectedProject?.name || routeProjectName;
  const headerSiteName = selectedSite?.name || routeSiteName;

  const metrics = useMemo(() => {
    const publishedTemplates = templates.filter(
      (template) => template.status === "PUBLISHED",
    ).length;
    const activeAssignments = assignments.filter(
      (assignment) => assignment.is_active,
    ).length;
    const surveyedSites = sites.filter(
      (site) => site.survey?.status === "SUBMITTED",
    ).length;
    const pendingSites = sites.filter(
      (site) => site.survey?.required && site.survey?.status !== "SUBMITTED",
    ).length;
    return {
      publishedTemplates,
      activeAssignments,
      surveyedSites,
      pendingSites,
    };
  }, [templates, assignments, sites]);

  const syncBuilderToJson = useCallback(
    (nextBuilder, baseForm = templateForm) => {
      setTemplateBuilder(nextBuilder);
      setTemplateForm((prev) => ({
        ...prev,
        ...baseForm,
        schema_json: JSON.stringify(
          buildSchemaFromBuilder(nextBuilder, { ...prev, ...baseForm }),
          null,
          2,
        ),
      }));
    },
    [templateForm],
  );

  const updateTemplateForm = useCallback(
    (patch) => {
      setTemplateForm((prev) => {
        const next = { ...prev, ...patch };
        const nextBuilder = {
          ...templateBuilder,
          name: next.name,
          code: next.code,
          version: Number(next.version || 1),
          survey_type: next.survey_type,
        };
        setTemplateBuilder(nextBuilder);
        return {
          ...next,
          schema_json: JSON.stringify(
            buildSchemaFromBuilder(nextBuilder, next),
            null,
            2,
          ),
        };
      });
    },
    [templateBuilder],
  );

  const handleApplySchemaJson = () => {
    try {
      const parsed = JSON.parse(templateForm.schema_json || "{}");
      const normalized = normalizeTemplateSchema(parsed, templateForm);
      setTemplateBuilder(normalized);
      setTemplateForm((prev) => ({
        ...prev,
        name: normalized.name || prev.name,
        code: normalized.code || prev.code,
        survey_type: normalized.survey_type || prev.survey_type,
        version: normalized.version || prev.version,
        schema_json: JSON.stringify(
          buildSchemaFromBuilder(normalized, prev),
          null,
          2,
        ),
      }));
      setSuccess("Advanced JSON applied to builder");
      setError("");
    } catch (err) {
      setError(`Invalid survey schema JSON: ${err.message}`);
    }
  };

  const handleOpenCreateTemplate = () => {
    setEditingTemplateId(null);
    setTemplateForm(emptyTemplateForm);
    setTemplateBuilder(
      normalizeTemplateSchema(defaultTemplateSchema, emptyTemplateForm),
    );
    setTemplateBuilderTab("builder");
    setTemplateDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    const nextForm = {
      name: template.name || "",
      code: template.code || "",
      description: template.description || "",
      survey_type: template.survey_type || "PRE_INSTALL",
      status: template.status || "DRAFT",
      version: template.version || 1,
      schema_json: JSON.stringify(template.schema_json || {}, null, 2),
    };
    setEditingTemplateId(template.id);
    setTemplateForm(nextForm);
    setTemplateBuilder(
      normalizeTemplateSchema(template.schema_json || {}, nextForm),
    );
    setTemplateBuilderTab("builder");
    setTemplateDialogOpen(true);
  };

  const moveItem = (list, index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= list.length) return list;
    const next = [...list];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    return next;
  };

  const updateSection = (sectionIndex, patch) => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections[sectionIndex] = {
      ...nextBuilder.sections[sectionIndex],
      ...patch,
    };
    syncBuilderToJson(nextBuilder);
  };

  const updateField = (sectionIndex, fieldIndex, patch) => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections[sectionIndex].fields[fieldIndex] = {
      ...nextBuilder.sections[sectionIndex].fields[fieldIndex],
      ...patch,
    };
    syncBuilderToJson(nextBuilder);
  };

  const addSection = () => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections.push(
      createSection(`Section ${nextBuilder.sections.length + 1}`),
    );
    syncBuilderToJson(nextBuilder);
  };

  const duplicateSection = (sectionIndex) => {
    const nextBuilder = cloneJson(templateBuilder);
    const section = cloneJson(nextBuilder.sections[sectionIndex]);
    section.key = `${section.key}_copy_${Math.random().toString(36).slice(2, 5)}`;
    section.title = `${section.title} Copy`;
    nextBuilder.sections.splice(sectionIndex + 1, 0, section);
    syncBuilderToJson(nextBuilder);
  };

  const removeSection = (sectionIndex) => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections.splice(sectionIndex, 1);
    if (!nextBuilder.sections.length) nextBuilder.sections = [createSection()];
    syncBuilderToJson(nextBuilder);
  };

  const moveSection = (sectionIndex, direction) => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections = moveItem(
      nextBuilder.sections,
      sectionIndex,
      direction,
    );
    syncBuilderToJson(nextBuilder);
  };

  const addField = (sectionIndex, type = "text") => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections[sectionIndex].fields.push(createField(type));
    syncBuilderToJson(nextBuilder);
  };

  const duplicateField = (sectionIndex, fieldIndex) => {
    const nextBuilder = cloneJson(templateBuilder);
    const field = cloneJson(
      nextBuilder.sections[sectionIndex].fields[fieldIndex],
    );
    field.key = `${field.key}_copy_${Math.random().toString(36).slice(2, 5)}`;
    field.label = `${field.label} Copy`;
    nextBuilder.sections[sectionIndex].fields.splice(fieldIndex + 1, 0, field);
    syncBuilderToJson(nextBuilder);
  };

  const removeField = (sectionIndex, fieldIndex) => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections[sectionIndex].fields.splice(fieldIndex, 1);
    if (!nextBuilder.sections[sectionIndex].fields.length) {
      nextBuilder.sections[sectionIndex].fields = [
        createField("text", "New Field"),
      ];
    }
    syncBuilderToJson(nextBuilder);
  };

  const moveField = (sectionIndex, fieldIndex, direction) => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections[sectionIndex].fields = moveItem(
      nextBuilder.sections[sectionIndex].fields,
      fieldIndex,
      direction,
    );
    syncBuilderToJson(nextBuilder);
  };

  const updateFieldOption = (sectionIndex, fieldIndex, optionIndex, patch) => {
    const nextBuilder = cloneJson(templateBuilder);
    nextBuilder.sections[sectionIndex].fields[fieldIndex].options[optionIndex] =
      {
        ...nextBuilder.sections[sectionIndex].fields[fieldIndex].options[
          optionIndex
        ],
        ...patch,
      };
    syncBuilderToJson(nextBuilder);
  };

  const addFieldOption = (sectionIndex, fieldIndex) => {
    const nextBuilder = cloneJson(templateBuilder);
    const options =
      nextBuilder.sections[sectionIndex].fields[fieldIndex].options || [];
    options.push({
      label: `Option ${options.length + 1}`,
      value: `OPTION_${options.length + 1}`,
    });
    nextBuilder.sections[sectionIndex].fields[fieldIndex].options = options;
    syncBuilderToJson(nextBuilder);
  };

  const removeFieldOption = (sectionIndex, fieldIndex, optionIndex) => {
    const nextBuilder = cloneJson(templateBuilder);
    const options =
      nextBuilder.sections[sectionIndex].fields[fieldIndex].options || [];
    options.splice(optionIndex, 1);
    nextBuilder.sections[sectionIndex].fields[fieldIndex].options = options;
    syncBuilderToJson(nextBuilder);
  };

  const handleSaveTemplate = async () => {
    if (!validateTemplate()) return;
    setSaving(true);
    try {
      const schemaPayload = buildSchemaFromBuilder(
        templateBuilder,
        templateForm,
      );
      const payload = {
        ...templateForm,
        version: Number(templateForm.version || 1),
        schema_json: schemaPayload,
      };
      if (editingTemplateId) {
        await patchRequest(
          `${SURVEYS_API}/templates/${editingTemplateId}`,
          payload,
          token,
          activeOrg,
        );
        setSuccess("Survey template updated");
      } else {
        await postRequest(
          `${SURVEYS_API}/templates`,
          payload,
          token,
          activeOrg,
        );
        setSuccess("Survey template created");
      }
      setTemplateDialogOpen(false);
      await loadData();
    } catch (err) {
      const message =
        err?.response?.data?.error || "Unable to save survey template";

      const newErrors = {};

      if (message.toLowerCase().includes("name")) {
        newErrors.name = " name already exists";
      }

      if (message.toLowerCase().includes("code")) {
        newErrors.code = "code already exists";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors((prev) => ({
          ...prev,
          ...newErrors,
        }));
        return;
      }

      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAssignment = async () => {
    if (!validateAssignment()) return;
    setSaving(true);
    try {
      const payload = {
        ...assignmentForm,
        site_id: assignmentForm.site_id || null,
      };
      await postRequest(
        `${SURVEYS_API}/assignments`,
        payload,
        token,
        activeOrg,
      );
      setAssignmentDialogOpen(false);
      setAssignmentForm(emptyAssignmentForm);
      setSuccess("Survey assignment created");
      await loadData();
    } catch (err) {
      const message =
        err?.response?.data?.error || "Unable to save survey template";

      // const newErrors = {};

      // if (message.toLowerCase().includes("name")) {
      //   newErrors.project_id = " name already exists";
      // }

      // if (message.toLowerCase().includes("code")) {
      //   newErrors.template_id = "code already exists";
      // }

      // if (Object.keys(newErrors).length > 0) {
      //   setErrors((prev) => ({
      //     ...prev,
      //     ...newErrors,
      //   }));
      //   return;
      // }

      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const activeAssignments = assignments.filter(
    (assignment) => assignment.is_active,
  );

  const handleOpenResponse = (response) => {
    setSelectedResponse(response);
    setResponseDialogOpen(true);
  };

  const handleDownloadSurveyReport = async (response) => {
    try {
      const download = await axios.get(
        `${SURVEYS_API}/responses/${response.id}/report.pdf`,
        {
          responseType: "blob",
          headers: buildAuthHeaders(token, activeOrg),
        },
      );
      const blobUrl = window.URL.createObjectURL(new Blob([download.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `site-survey-report-${response.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to download survey report"));
    }
  };

  const selectedResponseIndex = selectedResponse
    ? siteResponses.findIndex((response) => response.id === selectedResponse.id)
    : -1;

  return (
    <PageShell
      title={headerSiteName ? `${headerSiteName} Surveys` : "Site Surveys"}
      subtitle={
        headerProjectName
          ? `Configure dynamic site survey templates, assignments, and field submissions for ${headerProjectName}.`
          : "Configure dynamic site survey templates, assign them to rollout programs, and track field submissions coming from mobile operators."
      }
      leadingAction={
        showBackToSites ? (
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#DBEAFE",
              fontWeight: 800,
              textTransform: "none",
              px: 0,
              minWidth: "auto",
            }}
          >
            Back to Sites
          </Button>
        ) : null
      }
      actions={
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
          {canCreateTemplate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateTemplate}
              sx={actionButtonSx}
            >
              Add Template
            </Button>
          ) : null}
          {canAssignTemplate ? (
            <Button
              variant="contained"
              startIcon={<AssignmentTurnedInRoundedIcon />}
              onClick={() => setAssignmentDialogOpen(true)}
              sx={actionButtonSx}
            >
              Assign Template
            </Button>
          ) : null}
        </Stack>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          p: 0.75,
          mb: 2,
          position: "sticky",
          top: 12,
          zIndex: 2,
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Tabs
          value={workspaceTab}
          onChange={(_, value) => setWorkspaceTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              fontWeight: 900,
              textTransform: "none",
              fontSize: 14,
              borderRadius: 999,
              px: 2.1,
              mr: 0.8,
              color: "#475569",
            },
            "& .Mui-selected": {
              color: "#0F172A !important",
              backgroundColor: "#EFF6FF",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          <Tab value="responses" label="Responses" />
          <Tab value="overview" label="Overview" />
          <Tab value="templates" label="Templates" />
          <Tab value="assignments" label="Assignments" />
        </Tabs>
      </Paper>

      {loading ? (
        <SurveysSkeleton />
      ) : (
        <Stack spacing={2.25}>
          {workspaceTab === "responses" ? (
            <Grid container spacing={2}>
              <Grid item xs={12} lg={8} sx={{ width: "100%" }}>
                <SectionPanel eyebrow="Responses" title="Field Survey Activity">
                  <Stack direction={{ xs: "column", lg: "row" }} spacing={1.2}>
                    <TextField
                      select
                      fullWidth
                      label="Project"
                      value={selectedProjectId}
                      onChange={(e) => {
                        setSelectedProjectId(e.target.value);
                        setSelectedSiteId("");
                      }}
                    >
                      <MenuItem value="">All Projects</MenuItem>
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Autocomplete
                      fullWidth
                      options={visibleSites}
                      value={
                        visibleSites.find(
                          (site) => String(site.id) === String(selectedSiteId),
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        setSelectedSiteId(newValue ? newValue.id : "");
                      }}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Site"
                          placeholder="Search or select a site"
                        />
                      )}
                      noOptionsText="No sites found"
                    />
                  </Stack>

                  {selectedSite ? (
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 2.5,
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PlaceRoundedIcon sx={{ color: "#2563EB" }} />
                          <Box>
                            <Typography
                              sx={{ fontWeight: 900, color: "#0F172A" }}
                            >
                              {selectedSite.name}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 12.5, color: "#64748B" }}
                            >
                              {selectedSite.survey?.template?.name ||
                                "No template assigned"}{" "}
                              • {selectedSite.survey?.status || "No survey yet"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={0.8}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Chip
                            size="small"
                            label={`${siteResponses.length} responses`}
                            sx={{ fontWeight: 800 }}
                          />
                          {selectedSite.survey?.response_count ? (
                            <Chip
                              size="small"
                              label={`${selectedSite.survey.response_count} total submissions`}
                              sx={{ fontWeight: 800 }}
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                    </Paper>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      Choose a project and site first. Responses stay visible
                      here by default so field activity is always easy to find.
                    </Alert>
                  )}

                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    {siteResponses.map((response, responseIndex) => (
                      <Paper
                        key={response.id}
                        elevation={0}
                        onClick={() => handleOpenResponse(response)}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          border: "1px solid #E2E8F0",
                          cursor: "pointer",
                          transition:
                            "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            borderColor: "#BFDBFE",
                            boxShadow: "0 12px 24px rgba(37,99,235,0.08)",
                          },
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          spacing={1}
                        >
                          <Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                label={`#${String(responseIndex + 1).padStart(2, "0")}`}
                                size="small"
                                sx={{
                                  fontWeight: 900,
                                  backgroundColor: "#EFF6FF",
                                  color: "#1D4ED8",
                                }}
                              />
                              <Typography
                                sx={{ fontWeight: 900, color: "#0F172A" }}
                              >
                                {response.template?.name || "Survey Response"}
                              </Typography>
                            </Stack>
                            <Typography
                              sx={{
                                mt: 0.35,
                                fontSize: 12.5,
                                color: "#64748B",
                              }}
                            >
                              {response.operator?.name || "Field Operator"} •{" "}
                              {formatDateTime(
                                response.submitted_at ||
                                  response.updated_at ||
                                  response.created_at,
                              )}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {statusChip(response.status)}
                            <Chip
                              label={formatDateTime(
                                response.submitted_at ||
                                  response.updated_at ||
                                  response.created_at,
                              )}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                backgroundColor: "#F8FAFC",
                              }}
                            />
                          </Stack>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={0.8}
                          sx={{ mt: 1.2 }}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Chip
                            size="small"
                            icon={<ChecklistRoundedIcon />}
                            label={`${Object.keys(response.response_json || {}).length} answers`}
                            sx={{ fontWeight: 800 }}
                          />
                          {response.media?.length ? (
                            <Chip
                              size="small"
                              icon={<RuleRoundedIcon />}
                              label={`${response.media.length} media`}
                              sx={{ fontWeight: 800 }}
                            />
                          ) : null}
                          {response.captured_latitude !== null &&
                          response.captured_longitude !== null ? (
                            <Chip
                              size="small"
                              icon={<PlaceRoundedIcon />}
                              label={`${response.captured_latitude}, ${response.captured_longitude}`}
                              sx={{ fontWeight: 800 }}
                            />
                          ) : null}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 1.15 }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenResponse(response);
                            }}
                            sx={{ fontWeight: 800, textTransform: "none" }}
                          >
                            View full response
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PictureAsPdfRoundedIcon />}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDownloadSurveyReport(response);
                            }}
                            sx={{ fontWeight: 800, textTransform: "none" }}
                          >
                            Download PDF
                          </Button>
                        </Stack>
                      </Paper>
                    ))}
                    {selectedSiteId && !siteResponses.length ? (
                      <Alert severity="info">
                        No survey responses found for this site yet.
                      </Alert>
                    ) : null}
                  </Stack>
                </SectionPanel>
              </Grid>

              <Grid item xs={12} lg={4} sx={{ width: "100%" }}>
                <Stack spacing={2}>
                  <SectionPanel
                    eyebrow="Workspace Metrics"
                    title="Survey Snapshot"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.2,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SummaryCard
                          title="Sites Surveyed"
                          value={metrics.surveyedSites}
                          subtitle="Submitted from mobile app"
                        />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SummaryCard
                          title="Pending Survey"
                          value={metrics.pendingSites}
                          subtitle="Waiting for field capture"
                        />
                      </Box>
                    </Box>
                  </SectionPanel>

                  <SectionPanel
                    eyebrow="Assignments"
                    title="Active Survey Rollout"
                  >
                    <Stack spacing={1.1}>
                      {activeAssignments.slice(0, 4).map((assignment) => (
                        <Paper
                          key={assignment.id}
                          elevation={0}
                          sx={{
                            p: 1.3,
                            borderRadius: 2.5,
                            border: "1px solid #E2E8F0",
                            backgroundColor: "#F8FAFC",
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <Box>
                              <Typography
                                sx={{ fontWeight: 900, color: "#0F172A" }}
                              >
                                {assignment.template?.name || "Template"}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.35,
                                  fontSize: 12.5,
                                  color: "#64748B",
                                }}
                              >
                                {assignment.project?.name || "Project"}{" "}
                                {assignment.site?.name
                                  ? `• ${assignment.site.name}`
                                  : "• Project-wide"}
                              </Typography>
                            </Box>
                            {statusChip(assignment.survey_type)}
                          </Stack>
                        </Paper>
                      ))}
                      {!activeAssignments.length ? (
                        <Alert severity="info">
                          No active survey assignments yet.
                        </Alert>
                      ) : null}
                    </Stack>
                  </SectionPanel>
                </Stack>
              </Grid>
            </Grid>
          ) : null}

          {workspaceTab === "overview" ? (
            <>
              <Box display="flex" flexWrap="wrap" gap={2}>
                {[
                  {
                    title: "Published Templates",
                    value: metrics.publishedTemplates,
                    subtitle: "Ready for field rollout",
                  },
                  {
                    title: "Active Assignments",
                    value: metrics.activeAssignments,
                    subtitle: "Project or site bindings live now",
                  },
                  {
                    title: "Sites Surveyed",
                    value: metrics.surveyedSites,
                    subtitle: "Submitted from mobile app",
                  },
                  {
                    title: "Pending Survey",
                    value: metrics.pendingSites,
                    subtitle: "Required before next field step",
                  },
                ].map((card, index) => (
                  <Box
                    key={index}
                    flex="1 1 calc(25% - 16px)"
                    sx={{
                      minWidth: {
                        xs: "100%",
                        sm: "48%",
                        md: "23%",
                      },
                    }}
                  >
                    <SummaryCard {...card} />
                  </Box>
                ))}
              </Box>

              <Box display="flex" flexWrap="wrap" gap={2}>
                {/* LEFT SIDE */}
                <Box
                  sx={{
                    flex: { xs: "1 1 100%", xl: "1 1 58%" },
                    maxWidth: { xs: "100%", xl: "58%" },
                  }}
                >
                  <SectionPanel eyebrow="Templates" title="Survey Templates">
                    <Box display="flex" flexWrap="wrap" gap={1.5}>
                      {templates.map((template) => (
                        <Box
                          key={template.id}
                          sx={{
                            flex: {
                              xs: "1 1 100%",
                              md: "1 1 calc(50% - 12px)",
                            },
                          }}
                        >
                          <TemplateCard
                            template={template}
                            onEdit={handleEditTemplate}
                            canEdit={canUpdateTemplate}
                          />
                        </Box>
                      ))}

                      {!templates.length && (
                        <Box width="100%">
                          <Alert severity="info">
                            No survey templates created yet.
                          </Alert>
                        </Box>
                      )}
                    </Box>
                  </SectionPanel>
                </Box>

                {/* RIGHT SIDE */}
                <Box
                  sx={{
                    flex: { xs: "1 1 100%", xl: "1 1 40%" },
                    maxWidth: { xs: "100%", xl: "40%" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <SectionPanel
                    eyebrow="Assignments"
                    title="Active Survey Rollout"
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1.2}
                      sx={{
                        height: { xs: "500px", md: "975px", xl: "645px" },
                        overflow: "auto",
                      }}
                    >
                      {activeAssignments.map((assignment) => (
                        <Paper
                          key={assignment.id}
                          sx={{
                            p: 1.4,
                            borderRadius: 2.5,
                            border: "1px solid #E2E8F0",
                            backgroundColor: "#F8FAFC",
                            width: "100%",
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={1}
                          >
                            <Box>
                              <Typography sx={{ fontWeight: 900 }}>
                                {assignment.template?.name || "Template"}
                              </Typography>

                              <Typography
                                sx={{ fontSize: 12.5, color: "#64748B" }}
                              >
                                {assignment.project?.name || "Project"} •{" "}
                                {assignment.site?.name || "Project-wide"}
                              </Typography>
                            </Box>

                            {statusChip(assignment.survey_type)}
                          </Box>

                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={0.8}
                            mt={1.1}
                          >
                            {assignment.is_required && (
                              <Chip size="small" label="Required" />
                            )}
                            {assignment.must_complete_before_commissioning && (
                              <Chip
                                size="small"
                                label="Gate before commissioning"
                              />
                            )}
                            {assignment.allow_multiple_submissions && (
                              <Chip size="small" label="Multi-submit" />
                            )}
                          </Box>
                        </Paper>
                      ))}

                      {!activeAssignments.length && (
                        <Alert severity="info">
                          No active survey assignments yet.
                        </Alert>
                      )}
                    </Box>
                  </SectionPanel>
                  <SectionPanel eyebrow="Responses" title="Field Activity">
                    <Alert severity="info">
                      The operational response search now lives in the{" "}
                      <strong>Responses</strong> tab so submissions are
                      immediately visible without scrolling through the full
                      workspace.
                    </Alert>
                  </SectionPanel>
                </Box>
              </Box>
            </>
          ) : null}

          {workspaceTab === "templates" ? (
            <SectionPanel eyebrow="Templates" title="Survey Templates">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {templates.map((template) => (
                  <Box key={template.id}>
                    <TemplateCard
                      template={template}
                      onEdit={handleEditTemplate}
                      canEdit={canUpdateTemplate}
                    />
                  </Box>
                ))}
                {!templates.length ? (
                  <Box>
                    <Alert severity="info">
                      No survey templates created yet.
                    </Alert>
                  </Box>
                ) : null}
              </Box>
            </SectionPanel>
          ) : null}

          {workspaceTab === "assignments" ? (
            <SectionPanel eyebrow="Assignments" title="Active Survey Rollout">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {activeAssignments.map((assignment) => (
                  <Box>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.6,
                        height: "100%",
                        borderRadius: 2.5,
                        border: "1px solid #E2E8F0",
                        backgroundColor: "#F8FAFC",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Box>
                          <Typography
                            sx={{ fontWeight: 900, color: "#0F172A" }}
                          >
                            {assignment.template?.name || "Template"}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.35, fontSize: 12.5, color: "#64748B" }}
                          >
                            {assignment.project?.name || "Project"}{" "}
                            {assignment.site?.name
                              ? `• ${assignment.site.name}`
                              : "• Project-wide"}
                          </Typography>
                        </Box>
                        {statusChip(assignment.survey_type)}
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={0.8}
                        sx={{ mt: 1.1 }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {assignment.is_required ? (
                          <Chip
                            size="small"
                            label="Required"
                            sx={{ fontWeight: 800 }}
                          />
                        ) : null}
                        {assignment.must_complete_before_commissioning ? (
                          <Chip
                            size="small"
                            label="Gate before commissioning"
                            sx={{ fontWeight: 800 }}
                          />
                        ) : null}
                        {assignment.allow_multiple_submissions ? (
                          <Chip
                            size="small"
                            label="Multi-submit"
                            sx={{ fontWeight: 800 }}
                          />
                        ) : null}
                      </Stack>
                    </Paper>
                  </Box>
                ))}
                {!activeAssignments.length ? (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      No active survey assignments yet.
                    </Alert>
                  </Grid>
                ) : null}
              </Box>
            </SectionPanel>
          ) : null}
        </Stack>
      )}

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* LEFT - TITLE */}
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 18,
                color: "#fff",
              }}
            >
              {editingTemplateId
                ? "Edit Survey Template"
                : "Create Survey Template"}
            </Typography>

            {/* ADD THIS RIGHT SIDE ICON */}
            <IconButton
              onClick={() => setTemplateDialogOpen(false)}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.18)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.25 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: 2,
              width: "100%",
            }}
          >
            {/* ================= LEFT SIDE (BUILDER) ================= */}
            <Box
              sx={{
                flex: 3,
                width: "100%",
                minWidth: 0,
              }}
            >
              {/* -------- TOP FORM -------- */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.6,
                  borderRadius: 3,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#F8FAFC",
                  mb: 3,
                  mt: 2,
                }}
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  <Box sx={{ flex: "1 3 250px" }}>
                    <TextField
                      fullWidth
                      required
                      label="Template Name"
                      value={templateForm.name}
                      onChange={(e) => {
                        updateTemplateForm({ name: e.target.value });
                        setErrors((prev) => ({
                          ...prev,
                          name: "",
                        }));
                      }}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 180px" }}>
                    <TextField
                      fullWidth
                      required
                      label="Code"
                      value={templateForm.code}
                      onChange={(e) => {
                        updateTemplateForm({
                          code: e.target.value.toUpperCase(),
                        });
                        setErrors((prev) => ({
                          ...prev,
                          code: "",
                        }));
                      }}
                      error={!!errors.code}
                      helperText={errors.code}
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 120px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Version"
                      value={templateForm.version}
                      onChange={(e) =>
                        updateTemplateForm({ version: e.target.value })
                      }
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 180px" }}>
                    <TextField
                      select
                      fullWidth
                      label="Status"
                      value={templateForm.status}
                      onChange={(e) =>
                        updateTemplateForm({ status: e.target.value })
                      }
                    >
                      {TEMPLATE_STATUSES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item.replace(/_/g, " ")}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box sx={{ flex: "1 1 250px" }}>
                    <TextField
                      select
                      fullWidth
                      label="Survey Type"
                      value={templateForm.survey_type}
                      onChange={(e) =>
                        updateTemplateForm({ survey_type: e.target.value })
                      }
                    >
                      {SURVEY_TYPES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item.replace(/_/g, " ")}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box sx={{ flex: "2 1 400px" }}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={templateForm.description}
                      onChange={(e) =>
                        updateTemplateForm({ description: e.target.value })
                      }
                    />
                  </Box>
                </Box>
              </Paper>

              {/* -------- BUILDER -------- */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #E2E8F0",
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={templateBuilderTab}
                  onChange={(_, value) => setTemplateBuilderTab(value)}
                  sx={{
                    px: 1.5,
                    pt: 1,
                    borderBottom: "1px solid #E2E8F0",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Tab
                    value="builder"
                    label="Visual Builder"
                    sx={{ fontWeight: 900 }}
                  />
                  <Tab
                    value="json"
                    label="Advanced JSON"
                    sx={{ fontWeight: 900 }}
                  />
                </Tabs>

                {templateBuilderTab === "builder" ? (
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={1.6}>
                      {templateBuilder.sections.map((section, sectionIndex) => (
                        <Box key={sectionIndex} sx={{ mb: 2 }}>
                          {/* Section fields */}
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1.4 }}
                          >
                            <Box sx={{ flex: "1 1 250px" }}>
                              <TextField
                                fullWidth
                                label="Section Title"
                                value={section.title}
                                onChange={(e) =>
                                  updateSection(sectionIndex, {
                                    title: e.target.value,
                                  })
                                }
                              />
                            </Box>

                            <Box sx={{ flex: "1 1 250px" }}>
                              <TextField
                                fullWidth
                                label="Section Key"
                                value={section.key}
                                onChange={(e) =>
                                  updateSection(sectionIndex, {
                                    key: e.target.value,
                                  })
                                }
                              />
                            </Box>

                            <Box sx={{ flex: "2 1 400px" }}>
                              <TextField
                                fullWidth
                                label="Section Description"
                                value={section.description || ""}
                                onChange={(e) =>
                                  updateSection(sectionIndex, {
                                    description: e.target.value,
                                  })
                                }
                              />
                            </Box>
                          </Box>

                          {/* Fields */}
                          <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                            {section.fields.map((field, fieldIndex) => (
                              <Paper
                                key={fieldIndex}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2.5,
                                  border: "1px solid #E2E8F0",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1.3,
                                  }}
                                >
                                  <TextField
                                    fullWidth
                                    label="Field Label"
                                    value={field.label || ""}
                                    onChange={(e) =>
                                      updateField(sectionIndex, fieldIndex, {
                                        label: e.target.value,
                                      })
                                    }
                                  />

                                  <Box sx={{ flex: "1 1 180px" }}>
                                    <TextField
                                      fullWidth
                                      label="Field Key"
                                      value={field.key || ""}
                                      onChange={(e) =>
                                        updateField(sectionIndex, fieldIndex, {
                                          key: e.target.value,
                                        })
                                      }
                                    />
                                  </Box>

                                  <Box sx={{ flex: "1 1 180px" }}>
                                    <TextField
                                      select
                                      fullWidth
                                      label="Field Type"
                                      value={field.type}
                                      onChange={(e) =>
                                        updateField(
                                          sectionIndex,
                                          fieldIndex,
                                          createField(e.target.value),
                                        )
                                      }
                                    >
                                      {FIELD_TYPES.map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {type}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Box>

                                  <Box
                                    sx={{
                                      flex: "1 1 120px",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={Boolean(field.required)}
                                          onChange={(e) =>
                                            updateField(
                                              sectionIndex,
                                              fieldIndex,
                                              { required: e.target.checked },
                                            )
                                          }
                                        />
                                      }
                                      label="Required"
                                    />
                                  </Box>
                                </Box>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ p: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={20}
                      value={templateForm.schema_json}
                    />
                  </Box>
                )}
              </Paper>
            </Box>

            {/* ================= RIGHT SIDE (PREVIEW) ================= */}
            <Box
              sx={{
                flex: 1,
                width: "100%",
                minWidth: 280,
              }}
            >
              <TemplateBuilderPreview
                schema={buildSchemaFromBuilder(templateBuilder, templateForm)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.2 }}>
          <Button
            onClick={() => !saving && setTemplateDialogOpen(false)}
            disabled={saving}
            sx={{
              color: "#64748B",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#F1F5F9",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={saving}
            sx={{
              position: "relative",
              minWidth: 150,
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              boxShadow: "0 8px 20px rgba(245,158,11,0.25)",
              color: "#1E293B",
              fontWeight: 800,
              textTransform: "none",
              px: 2.5,
              borderRadius: 2,
              "&:hover": {
                background: "linear-gradient(135deg, #FCD34D, #D97706)",
              },
            }}
          >
            {saving ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Save Template"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Create Survey Assignment */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        fullWidth
        // maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* LEFT - TITLE */}
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 18,
                color: "#fff",
              }}
            >
              Create Survey Assignment
            </Typography>

            {/* ADD THIS RIGHT SIDE ICON */}
            <IconButton
              onClick={() => setAssignmentDialogOpen(false)}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.18)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Template */}
            <Box sx={{ width: "100%", mt: 2 }}>
              <TextField
                select
                fullWidth
                required
                label="Template"
                value={assignmentForm.template_id}
                onChange={(e) => {
                  setAssignmentForm((prev) => ({
                    ...prev,
                    template_id: e.target.value,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    template_id: "",
                  }));
                }}
                error={!!errors.template_id}
                helperText={errors.template_id}
              >
                {templates
                  .filter((template) => template.status === "PUBLISHED")
                  .map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>

            {/* Project */}
            <Box sx={{ width: "100%" }}>
              <TextField
                select
                fullWidth
                required
                label="Project"
                value={assignmentForm.project_id}
                onChange={(e) => {
                  setAssignmentForm((prev) => ({
                    ...prev,
                    project_id: e.target.value,
                    site_id: "",
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    project_id: "",
                  }));
                }}
                error={!!errors.project_id}
                helperText={errors.project_id}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Site */}
            <Box sx={{ width: "100%" }}>
              <TextField
                select
                fullWidth
                label="Site (optional)"
                value={assignmentForm.site_id}
                onChange={(e) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    site_id: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Project-wide assignment</MenuItem>
                {sites
                  .filter(
                    (site) =>
                      !assignmentForm.project_id ||
                      site.project_id === assignmentForm.project_id,
                  )
                  .map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>

            {/* Survey Type */}
            <Box sx={{ width: "100%" }}>
              <TextField
                select
                fullWidth
                required
                label="Survey Type"
                value={assignmentForm.survey_type}
                onChange={(e) => {
                  setAssignmentForm((prev) => ({
                    ...prev,
                    survey_type: e.target.value,
                  }));
                }}
              >
                {SURVEY_TYPES.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Info Card */}
            <Box sx={{ width: "100%" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.6,
                  borderRadius: 3,
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 900,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Assignment Behavior
                </Typography>
                <Typography
                  sx={{
                    mt: 0.55,
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: "#475569",
                  }}
                >
                  Use these switches to decide whether the survey is mandatory,
                  repeatable, and whether it blocks commissioning.
                </Typography>
              </Paper>
            </Box>

            {/* Required */}
            <Box sx={{ width: { xs: "100%", md: "25%" } }}>
              <TextField
                select
                fullWidth
                label="Required"
                value={String(assignmentForm.is_required)}
                onChange={(e) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    is_required: e.target.value === "true",
                  }))
                }
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
            </Box>

            {/* Allow Repeat */}
            <Box sx={{ width: { xs: "100%", md: "32%" } }}>
              <TextField
                select
                fullWidth
                label="Allow Repeat Submit"
                value={String(assignmentForm.allow_multiple_submissions)}
                onChange={(e) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    allow_multiple_submissions: e.target.value === "true",
                  }))
                }
              >
                <MenuItem value="false">No</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
              </TextField>
            </Box>

            {/* Block Commission */}
            <Box sx={{ width: { xs: "100%", md: "32%" } }}>
              <TextField
                select
                fullWidth
                label="Block Commissioning Until Survey"
                value={String(
                  assignmentForm.must_complete_before_commissioning,
                )}
                onChange={(e) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    must_complete_before_commissioning:
                      e.target.value === "true",
                  }))
                }
              >
                <MenuItem value="false">No</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.2 }}>
          <Button
            onClick={() => !saving && setAssignmentDialogOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveAssignment}
            disabled={saving}
            sx={{
              minWidth: 160,
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
            }}
          >
            {saving ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Create Assignment"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          {selectedResponse?.template?.name || "Survey Response Details"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          {selectedResponse ? (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={1}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  {selectedResponseIndex >= 0 ? (
                    <Chip
                      label={`#${String(selectedResponseIndex + 1).padStart(2, "0")}`}
                      size="small"
                      sx={{
                        fontWeight: 900,
                        backgroundColor: "#EFF6FF",
                        color: "#1D4ED8",
                      }}
                    />
                  ) : null}
                  {statusChip(selectedResponse.status)}
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfRoundedIcon />}
                  onClick={() => handleDownloadSurveyReport(selectedResponse)}
                  sx={{
                    fontWeight: 800,
                    textTransform: "none",
                    alignSelf: { xs: "stretch", md: "auto" },
                  }}
                >
                  Download Survey Report
                </Button>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  p: 1.6,
                  borderRadius: 3,
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#64748B",
                        textTransform: "uppercase",
                      }}
                    >
                      Site
                    </Typography>
                    <Typography
                      sx={{ mt: 0.45, fontWeight: 900, color: "#0F172A" }}
                    >
                      {selectedSite?.name || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#64748B",
                        textTransform: "uppercase",
                      }}
                    >
                      Project
                    </Typography>
                    <Typography
                      sx={{ mt: 0.45, fontWeight: 900, color: "#0F172A" }}
                    >
                      {selectedProject?.name || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#64748B",
                        textTransform: "uppercase",
                      }}
                    >
                      Operator
                    </Typography>
                    <Typography
                      sx={{ mt: 0.45, color: "#0F172A", fontWeight: 800 }}
                    >
                      {selectedResponse.operator?.name || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#64748B",
                        textTransform: "uppercase",
                      }}
                    >
                      Submitted
                    </Typography>
                    <Typography
                      sx={{ mt: 0.45, color: "#0F172A", fontWeight: 800 }}
                    >
                      {formatDateTime(
                        selectedResponse.submitted_at ||
                          selectedResponse.updated_at ||
                          selectedResponse.created_at,
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#64748B",
                        textTransform: "uppercase",
                      }}
                    >
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {statusChip(selectedResponse.status)}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: 1.8, borderRadius: 3, border: "1px solid #E2E8F0" }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 900,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Captured Answers
                </Typography>
                <Stack
                  divider={<Divider flexItem sx={{ borderColor: "#E2E8F0" }} />}
                  sx={{ mt: 1.2 }}
                >
                  {Object.entries(selectedResponse.response_json || {}).map(
                    ([key, value]) => {
                      if (key === "site_photo") {
                        return null;
                      }
                      return (
                        <Box key={key} sx={{ py: 1.25 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 900,
                              color: "#64748B",
                              textTransform: "uppercase",
                            }}
                          >
                            {key.replace(/_/g, " ")}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.45, color: "#0F172A", lineHeight: 1.7 }}
                          >
                            {formatAnswerValue(value)}
                          </Typography>
                        </Box>
                      );
                    },
                  )}
                  {!Object.keys(selectedResponse.response_json || {}).length ? (
                    <Typography sx={{ py: 1.5, color: "#64748B" }}>
                      No answer payload stored.
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: 1.8, borderRadius: 3, border: "1px solid #E2E8F0" }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 900,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Media Evidence
                </Typography>
                <Stack spacing={1.1} sx={{ mt: 1.2 }}>
                  {(selectedResponse.media || []).map((media, index) => (
                    <Paper
                      key={media.id || `${media.field_key}-${index}`}
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                        {media.field_key?.replace(/_/g, " ") ||
                          `Media ${index + 1}`}
                      </Typography>
                      {(media.download_url || media.file_url)?.startsWith(
                        "http",
                      ) ? (
                        <Box
                          component="img"
                          src={media.download_url || media.file_url}
                          alt={media.field_key || `media-${index + 1}`}
                          sx={{
                            mt: 1,
                            width: "100%",
                            maxHeight: 320,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid #CBD5E1",
                            backgroundColor: "#E2E8F0",
                          }}
                        />
                      ) : null}
                      {/* <Typography
                        sx={{
                          mt: 0.35,
                          fontSize: 12.5,
                          color: "#475569",
                          wordBreak: "break-all",
                        }}
                      >
                        {media.download_url || media.file_url}
                      </Typography> */}
                      {media.download_url || media.file_url ? (
                        <Button
                          size="small"
                          startIcon={<DownloadRoundedIcon />}
                          onClick={() =>
                            window.open(
                              media.download_url || media.file_url,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                          sx={{
                            mt: 0.8,
                            px: 0,
                            minWidth: 0,
                            fontWeight: 800,
                            textTransform: "none",
                          }}
                        >
                          Download media
                        </Button>
                      ) : null}
                      {media.captured_at ? (
                        <Typography
                          sx={{ mt: 0.35, fontSize: 12, color: "#64748B" }}
                        >
                          Captured {formatDateTime(media.captured_at)}
                        </Typography>
                      ) : null}
                    </Paper>
                  ))}
                  {!selectedResponse.media?.length ? (
                    <Typography sx={{ color: "#64748B" }}>
                      No media evidence attached.
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.2 }}>
          <Button onClick={() => setResponseDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
